import { useState } from 'react';
import { TaskPage } from '../../../components/workbook/TaskPage';
import { TextInput } from '../../../components/workbook/forms';
import { Callout } from '../../../components/workbook/Callout';

export default function CapacityWIPPage() {
  const [team, setTeam] = useState('');
  const [personWeeks, setPersonWeeks] = useState('');
  const [fixedCommitments, setFixedCommitments] = useState('');
  const [netCapacity, setNetCapacity] = useState('');
  const [nowItems, setNowItems] = useState(['', '', '']);
  const [nextItems, setNextItems] = useState(['', '']);
  const [laterItems, setLaterItems] = useState<string[]>(['']);

  const updateNowItem = (index: number, value: string) => {
    const updated = [...nowItems];
    updated[index] = value;
    setNowItems(updated);
  };

  const updateNextItem = (index: number, value: string) => {
    const updated = [...nextItems];
    updated[index] = value;
    setNextItems(updated);
  };

  const updateLaterItem = (index: number, value: string) => {
    const updated = [...laterItems];
    updated[index] = value;
    setLaterItems(updated);
  };

  const addLaterItem = () => {
    setLaterItems([...laterItems, '']);
  };

  return (
    <TaskPage
      phase="prioritize"
      title="Capacity & WIP Limits"
      pageNumber={2}
      instruction="Reality check. Calculate capacity and apply WIP limits. Note: WIP &gt; 3 increases switching cost; cap Now to protect flow."
      whyItMatters="Simple, visible limits lower cognitive load and keep attention on the intrinsic task—doing the most important work first."
      coachRail={{
        timing: "10 minutes",
        tips: "Max 3 items in Now. Keep it realistic. Capacity = available - fixed commitments.",
        debrief: "Does our Now list fit in capacity? Are we being realistic?"
      }}
    >
      <div className="space-y-6">
        <div className="bg-[var(--surface)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-4">Capacity Box</h3>
          <div className="grid grid-cols-2 gap-4">
            <TextInput
              label="Team"
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              placeholder="Team name or size"
            />
            <TextInput
              label="Person-weeks available this cycle (2-4 wks)"
              type="number"
              value={personWeeks}
              onChange={(e) => setPersonWeeks(e.target.value)}
              placeholder="e.g., 8"
            />
            <TextInput
              label="Fixed commitments already booked"
              type="number"
              value={fixedCommitments}
              onChange={(e) => setFixedCommitments(e.target.value)}
              placeholder="e.g., 2"
            />
            <TextInput
              label="Net capacity"
              type="number"
              value={netCapacity}
              onChange={(e) => setNetCapacity(e.target.value)}
              placeholder="Available - fixed"
              disabled
            />
          </div>
        </div>

        <div className="bg-[var(--slate-dim)] border border-[var(--slate)] p-6">
          <h3 className="text-lg font-semibold text-[var(--text-sec)] mb-4">Now/Next/Later Board</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h4 className="font-semibold text-[var(--text-sec)] mb-3">Now (max 3 parallel)</h4>
              <p className="text-xs text-[var(--text-sec)] mb-2">Fits inside net capacity</p>
              <div className="space-y-2">
                {nowItems.map((item, index) => (
                  <TextInput
                    key={index}
                    value={item}
                    onChange={(e) => updateNowItem(index, e.target.value)}
                    placeholder={`Now item ${index + 1}`}
                    className="text-sm"
                  />
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-[var(--text-sec)] mb-3">Next (queue)</h4>
              <p className="text-xs text-[var(--text-sec)] mb-2">Ready when Now clears</p>
              <div className="space-y-2">
                {nextItems.map((item, index) => (
                  <TextInput
                    key={index}
                    value={item}
                    onChange={(e) => updateNextItem(index, e.target.value)}
                    placeholder={`Next item ${index + 1}`}
                    className="text-sm"
                  />
                ))}
              </div>
            </div>
            <div>
              <h4 className="font-semibold text-[var(--text-sec)] mb-3">Later (parked w/ review date)</h4>
              <p className="text-xs text-[var(--text-sec)] mb-2">Deferred for now</p>
              <div className="space-y-2">
                {laterItems.map((item, index) => (
                  <TextInput
                    key={index}
                    value={item}
                    onChange={(e) => updateLaterItem(index, e.target.value)}
                    placeholder={`Later item ${index + 1}`}
                    className="text-sm"
                  />
                ))}
                <button
                  onClick={addLaterItem}
                  className="w-full py-1 px-2 border border-dashed border-[var(--slate)] rounded text-[var(--text-sec)] text-xs hover:border-[var(--slate)]"
                >
                  + Add
                </button>
              </div>
            </div>
          </div>
        </div>

        <Callout type="risk" phase="prioritize">
          <p className="text-sm">⚠️ WIP &gt; 3 increases switching cost. Cap Now to protect flow. If you have more than 3 items, move extras to Next.</p>
        </Callout>
      </div>
    </TaskPage>
  );
}

